<template>
  <v-row justify="space-between" align="center">
    <v-col v-if="showWarning" class="pt-6 mb-n2" cols="12">
      <v-card color="error" flat style="max-width: 1000px">
        <v-card-title class="white--text subtitle-1">
          <v-row>
            <v-col>
              {{ $t('scheduledRateChangeExistsWarning') }}
            </v-col>

            <v-col cols="auto">
              <v-btn class="my-n2" outlined dark @click="hideChangeExistsWarning">{{ $t('hide') }}</v-btn>
            </v-col>
          </v-row>
        </v-card-title>
      </v-card>
    </v-col>

    <v-col class="pt-6 mb-n2">
      <slot name="table-head.prepend" />
    </v-col>

    <v-col cols="auto" v-if="scheduledRateChangeAlreadyExistsForContract || showNewActivityButton">
      <Icon
      v-if="scheduledRateChangeAlreadyExistsForContract && !readonly"
      icon="mdi-delete-forever"
      :small="false"
      large
      @icon-clicked="deleteChangeButtonClicked"
      :tooltipText="$t('deleteScheduledRateChange')"></Icon>

      <Icon
      v-else-if="showNewActivityButton && !readonly"
      icon="mdi-plus"
      :small="false"
      :tooltipText="$t('newActivity')"
       @icon-clicked="newActivityClicked"/>
    </v-col>

    <v-col cols="12">
      <DataTable
      :tableHeightCalculationData="{ auto: true }"
      containerClass="pa-0"
      htmlIdentifier="contract-rate-change-activity-table"
      :showSearch="false"
      :headers="headers"
      :customCells="customCells"
      :items="contractActivityChangeBuilders"
      :itemClass="rowClass"
      tableKey="activityId"
      :propExpanded="expandedActivities">
        <template #activityStatus="{ item: activity }">
          <v-row no-gutters justify="center" :style="{ opacity: activity.isRemoved ? 0.4 : 1 }">
            <v-col cols="auto">
              <Icon
              :small="false"
              :icon="IconHelpers.getIconForObjectType(accountingCategoryFromInt(activity.activityTemplate.category))"
              :tooltipText="accountingCategoryFromInt(activity.activityTemplate.category)"/>
            </v-col>

            <v-col cols="auto">
              <Icon
              v-if="activity.displayNewIcon"
              small
              icon-color="info"
              icon="mdi-creation"
              :tooltipText="$t('newlyAddedActivity')"/>
            </v-col>

            <v-col cols="auto">
              <Icon
                v-if="activity.activityTemplate.costType !== CostType.Unspecified.value"
                small
                icon-color="info"
                :icon="costIcons(activity.activityTemplate.costType)"
                :tooltipText="CostTypeFromTemplate(activity.activityTemplate)" />
            </v-col>

            <v-col cols="auto">
              <Icon
              v-if="activity.recoveryMode?.value !== RecoveryMode.None.value && activity.recoveryMode !== RecoveryMode.None.value"
              small
              icon="mdi-cash-multiple"
              icon-color="success"
              :tooltipText="recoveryModeString(activity)" />
            </v-col>

            <v-col cols="auto">
              <Icon
              v-if="TemplateSpecialization.forInt(activity.activityTemplate.specialization).icon != null"
              :icon="TemplateSpecialization.forInt(activity.activityTemplate.specialization).icon"
              :tooltipText="TemplateSpecialization.forInt(activity.activityTemplate.specialization).name"
              :small="false"/>
            </v-col>

            <v-col cols="auto">
              <Icon
              v-if="activityDeductsFrom(activity)"
              icon="mdi-circle-multiple-outline"
              :tooltipText="deductionTooltip(activity)"
              small
              icon-color="success"
              @mouseenter.native="setHoveredParent(getDeductFromIdentifier(activity))"
              @mouseleave.native="clearHoveredParent"
              />
              <Icon
              v-if="hasDeductingChildren(activity)"
              icon="mdi-minus-circle-multiple-outline"
              :tooltipText="deductionParentTooltip(activity)"
              small
              icon-color="error"
              @mouseenter.native="setHoveredDeducting(activity)"
              @mouseleave.native="clearHoveredDeducting"
              />
            </v-col>
          </v-row>
        </template>

        <template #activityTemplateName="{ item: activity }">
          <span :style="styleTableCellText(activity)">{{ activity.activityTemplate?.name }}</span>
        </template>

        <template #counterpartyName="{ item: activity }">
          <Icon
          :style="{ opacity: activity.isRemoved ? 0.4 : 1 }"
          v-if="showInsuranceExpiryIcon(activity)"
          icon="mdi-shield-alert"
          :tooltipText="getInsuranceExpiryTooltip(activity)"/>

          <span v-if="counterpartySet(activity)" :style="styleTableCellText(activity)">
            <Icon
            :style="{ opacity: activity.isRemoved ? 0.4 : 1 }"
            iconColor="info"
            v-if="activity.runtimeCounterparty != null"
            icon="mdi-alpha-r-circle-outline"
            :tooltipText="`${$t('runtimeCounterparty')}: ${runtimeCounterparty(activity.runtimeCounterparty)}`"/>
            {{ counterpartyString(activity) }}
          </span>

          <span v-else>
            {{ $t('notSet') }}
            <Icon
            v-if="activity.runtimeCounterparty !== null"
            icon="mdi-alert"
            iconColor="error"
            tooltipTcolor="error"
            :tooltipText="$t('runtimeFieldNotSpecified', {
              runtimeField: runtimeCounterparty(activity.runtimeCounterparty)
            })"/>
          </span>
        </template>

        <template #recoveryMode="{ item: activity }">
          <Icon
          iconColor="info"
          :style="{ opacity: activity.isRemoved ? 0.4 : 1 }"
          v-if="activity.recoveryMode !== RecoveryMode.None.value"
          :icon="iconForRecoveryMode(activity.recoveryMode)"
          :tooltipText="tooltipForRecoveryMode(activity)"
          />
        </template>

        <template #businessEntityName="{ item: activity }">
          <span :style="styleTableCellText(activity)">{{ entityNameById[activity.activityTemplate?.businessEntityId] ?? '' }}</span>
        </template>

        <template #baseRate="{ item: activity }">
          <Icon
          v-if="activity.displayChangedIcon"
          icon="invoice-text-update"
          iconColor="info"/>
          <span v-if="activity.baseCost?.rate != null" :style="styleTableCellText(activity)">
            {{ formatMoney(activity.baseCost?.rate) }}
          </span>
          <span v-else :style="styleTableCellText(activity)">
            {{ $t('notApplicable') }}
          </span>
        </template>

        <template #payOn="{ item: activity }">
          <span v-if="activity.baseCost?.payOn != null" :style="styleTableCellText(activity)">
            {{ PayOn.fromInt(activity.baseCost?.payOn) }}
          </span>
          <span v-else :style="styleTableCellText(activity)">
            {{ $t('notApplicable') }}
          </span>
        </template>

        <template #payBy="{ item: activity }">
          <span v-if="activity.baseCost?.payBy != null" :style="styleTableCellText(activity)">
            {{ PayBy.fromInt(activity.baseCost?.payBy) }}
          </span>
          <span v-else :style="styleTableCellText(activity)">
            {{ $t('notApplicable') }}
          </span>
        </template>

        <template #modifier="{ item: activity }">
          <span :style="{ whiteSpace: 'nowrap', ...styleTableCellText(activity) }" v-if="activity.activityTemplate.modifier != null">
            {{ modifierFromInt(activity.activityTemplate.modifier) }}
          </span>
        </template>

        <template #rateSheetName="{ item: activity }">
          <Icon
          v-if="activity.displayChangedRateSheetIcon"
          icon="invoice-text-update"
          iconColor="info"/>
          <Icon
          v-if="activity.baseCost?.rateSheet"
          icon="mdi-note-text"
          margin="mb-1 mr-1"
          :iconColor="readonly || scheduledRateChangeAlreadyExistsForContract ? 'grey' : 'success'"
          @icon-clicked="rateSheetDetailClicked(activity)"/>
          <span v-if="activity.baseCost?.rateSheet?.name" :style="styleTableCellText(activity)">
            {{ getRateSheetName(activity) }}
          </span>
        </template>

        <template #actions="{ item: activity }">
          <v-row justify="center" dense class="flex-nowrap">
            <v-col v-if="!activity.isRemoved && activity.activityTemplate.specialization !== TemplateSpecialization.Obligation.value" cols="auto">
              <Icon icon="mdi-pencil" :tooltipText="$t('edit')" @icon-clicked="editActivityClicked(activity)" />
            </v-col>

            <v-col v-if="!activity.isRemoved && !detailsDisallowedForActivity(activity)" cols="auto">
              <Icon icon="mdi-plus" :tooltipText="$t('addDetail')" iconColor="success"
                @icon-clicked="addDetailClicked(activity)" />
            </v-col>

            <v-col v-if="!activity.isRemoved" cols="auto">
              <Icon icon="mdi-close" iconColor="grey darken-2" :tooltipText="$t('remove')"
                @icon-clicked="removeActivityClicked(activity)" />
            </v-col>

            <v-col v-else cols="auto">
              <Icon icon="mdi-arrow-u-left-top-bold" iconColor="grey darken-2" :tooltipText="$t('restore')"
                @icon-clicked="removeActivityClicked(activity)" />
            </v-col>
          </v-row>
        </template>

        <template #expanded-item="{ item: activity }">
          <v-row v-if="activity.isExpandable" class="my-1" dense>
            <v-col cols="12" sm="6" md="4" lg="3" xl="2" v-for="detail in activity.activityDetails" :key="detail.activityDetailId">
              <v-card outlined :style="styleDetailCard(detail)">
                <v-card-title class="py-0 px-3">
                  <v-row>
                    <v-col>
                      <span :style="{ fontSize: '14px', ...styleDetailText(detail) }">
                        <Icon
                          v-if="detail.displayNewIcon"
                          icon="mdi-creation"
                          iconColor="info"
                          :tooltipText="$t('newlyAddedDetail')"
                        />
                        {{ detail.product.name }}
                      </span>
                    </v-col>

                    <v-col cols="auto" v-if="allowRemoveDetail()">
                      <Icon
                        v-if="!detail.isRemoved"
                        icon="mdi-close"
                        iconColor="grey"
                        elementId="close"
                        margin=""
                        @icon-clicked="removeProductDetail(detail, activity)"
                        :tooltipText="$t('removeProductDetail')"
                      />
                      <Icon
                        v-else
                        icon="mdi-arrow-u-left-top-bold"
                        iconColor="secondary"
                        margin=""
                        @icon-clicked="restoreProductDetail(detail, activity)"
                        :tooltipText="$t('restore')"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>

                <v-card-text>
                  <v-row justify="end" no-gutters>
                    <v-col v-if="rateIsLowerThanBaseRate(detail, activity)">
                      <span class="error--text caption ml-n1 mr-2">
                        {{ $t('baseRateLowerThanActivity') }}
                      </span>
                    </v-col>

                    <v-col cols="auto">
                      <span :style="styleDetailText(detail)">
                        <Icon
                        v-if="detail.displayChangedIcon"
                        icon="invoice-text-update"
                        iconColor="info"/>
                        {{ formatMoney(detail.cost.rate) }}
                      </span>
                    </v-col>

                    <v-col cols="auto" v-if="!scheduledRateChangeAlreadyExistsForContract && !detail.isRemoved">
                      <Icon icon="mdi-pencil" @icon-clicked="editDetailRateClicked(detail)" margin="ml-2 mr-n1"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </DataTable>
    </v-col>

    <Dialog
    :stateId="dialogId"
    @close="closeDialog"
    @dialog-closing="closeDialog"
    @click:outside="closeDialog"
    :enforcePersistence="editingRateSheet"
    >

      <ContractActivityScheduledChangeForm
        v-if="editingActivity"
        :isImmediateRateChange="isImmediateRateChange"
        :activity="activityChangeBuilderBeingEdited"
        :contract="contract"
        :tract="tract"
        :allActivities="contractActivityChangeBuilders"
        @confirmed="onActivityChangeConfirmed"
      />

      <ContractDetailScheduledChangeForm
        v-if="editingDetail"
        :activity="activityChangeBuilderBeingEdited"
        :detail="detailChangeBuilderBeingEdited"
        :contract="contract"
        @confirmed="onDetailChangeConfirmed"
      />

      <ActivityForm
        v-if="creatingActivity"
        :contract="contract"
        :businessEntities="businessEntities"
        @close-form="closeDialog"
        @activity-updated="onActivityCreated"
        :isForScheduledRateChange="!isImmediateRateChange"
        :contractActivityChangeBuilders="contractActivityChangeBuilders"
        :namedRates="namedRates"
      />

      <DetailForm
        v-if="creatingDetail"
        :contractActivities="contractActivityChangeBuilders"
        :activity="activityChangeBuilderBeingEdited"
        :propDetail="detailChangeBuilderBeingEdited"
        :contract="contract"
        @cancel="closeDialog"
        @detail-mutated="onDetailAdded"
        @handle-detail-cost-warning="handleDetailCostWarning"
      />

      <ConfirmDialog
      v-if="deleteWarningDialogOpen"
      :title="$t('deleteScheduledRateChange')"
      :body="$t('confirmDeleteScheduledRateChangeBody')"
      @confirm="onConfirmDeleteChange"
      @cancel="closeDialog"
      />

      <RateSheetEditor
      v-if="viewingRateSheetDetails && !scheduledRateChangeAlreadyExistsForContract && !focusedActivityIsObligation"
      :activity="focusedActivity"
      :existingRateSheet="viewingRateSheetDetails"
      :initDefinition="viewingRateSheetDetails?.rateSheetDefinition"
      :contract="contract"
      :accountId="contract.destinationAccountId"
      @confirm="rateSheetConfirmed"
      @cancel="closeDialog"
      @editing-ratesheet="editingRateSheet = true"
      @close="closeDialog"
      />

      <RateSheetViewer
      v-if="viewingRateSheetDetails && (scheduledRateChangeAlreadyExistsForContract || focusedActivityIsObligation)"
      :rateSheet="viewingRateSheetDetails"
      :accountId="contract.destinationAccountId"
      @close="closeDialog"/>
    </Dialog>
  </v-row>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { cloneDeep, remove } from 'lodash'
import moment from 'moment'
import ActivityHeaders from '@/headers/Activity'
import { formatMoney } from '@/utils/NumericMutations.js'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter.js'
import {
  AccountingCategory, ActivityModifier, CostType, PayBy, PayOn, ProductionCostType,
  RecoveryMode, RuntimeCounterparty, TemplateSpecialization, ScheduledRateChangeStatus
} from '@/utils/Enumerations'
import { costTypeIconFromInt } from '@/utils/FinancialHelpers.js'
import IconHelpers from '@/utils/IconHelpers'
import { uniqueDialogId } from '@/utils/componentHelpers.js'
import { localeCompareAlphanumeric } from '@/utils/base'
import { helpers, theme } from '@/plugins/theme.js'
import colors from 'vuetify/lib/util/colors'
import RateSheet from '@/model/RateSheet.js'
import BaseCost from '@/model/BaseCost.js'
import { rateSheetLineFromServerToBuilder } from '@/components/activity/rate-sheet/RateSheet'

export default {
  name: 'ContractRateChangeActivityTable',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ContractActivityScheduledChangeForm: () => import('@/components/contract/scheduled-rate-change/ContractActivityScheduledChangeForm.vue'),
    ContractDetailScheduledChangeForm: () => import('@/components/contract/scheduled-rate-change/ContractDetailScheduledChangeForm.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ActivityForm: () => import('@/components/activity/ActivityForm.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    DetailForm: () => import('@/components/activity/activity-detail/DetailForm.vue'),
    RateSheetEditor: () => import('@/components/activity/rate-sheet/RateSheetEditor.vue'),
    RateSheetViewer: () => import('@/components/activity/rate-sheet/RateSheetViewer.vue')
  },

  data: () => ({
    CostType,
    costTypeIconFromInt,
    IconHelpers,
    PayBy,
    PayOn,
    ProductionCostType,
    RecoveryMode,
    TemplateSpecialization,
    themeHelpers: helpers,
    theme,

    contractActivityChangeBuilders: [],
    dialogId: uniqueDialogId('contract-activity-table-form'),
    chosenEffectiveDate: null,
    changeExistsWarningHidden: false,
    activityChangeBuilderBeingEdited: null,
    editingActivity: false,
    detailChangeBuilderBeingEdited: null,
    editingDetail: false,
    creatingActivity: false,
    deleteWarningDialogOpen: false,
    creatingDetail: false,
    deletedActivityIds: [],
    activityTemplates: [],
    hoveredDeductingActivityIdentifiers: [],
    hoveredParentActivityIdentifier: null,
    namedRates: undefined,
    viewingRateSheetDetails: undefined,
    accountRateSheetDefinitionsFetched: false,
    focusedActivity: null,
    editingRateSheet: false,
    tract: null
  }),

  props: {
    contract: {
      type: Object,
      required: true
    },

    isImmediateRateChange: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters('activity', ['allActivities', 'activitiesByEntity']),
    ...mapGetters('user', ['businessEntities']),
    ...mapGetters('scheduled-rate-change', ['contractScheduledRateChanges']),

    entityNameById () {
      return this.businessEntities.reduce((acc, entity) => {
        acc[entity.businessEntityId] = entity.name
        return acc
      }, {})
    },

    headers () {
      let headers = ActivityHeaders.contractActivityTableFormHeaders()
      if (this.noPayByVolume) {
        headers = headers.filter(h => h.value !== 'rateSheetName')
      }

      if (this.scheduledRateChangeAlreadyExistsForContract) {
        headers = headers.filter(h => h.value !== 'actions')
      }

      const noActivitiesRecover = this.contractActivityChangeBuilders.every(a => a.recoveryMode === RecoveryMode.None.value)
      if (noActivitiesRecover) {
        headers = headers.filter(h => h.value !== 'recoveryMode')
      }

      return headers
    },

    noPayByVolume () {
      return this.contractActivityChangeBuilders.every(a => PayBy.Volume.value !== a.baseCost?.payBy)
    },

    customCells () {
      return this.headers.map(h => ({ slotName: h.value, value: h.value }))
    },

    expandedActivities () {
      return this.contractActivityChangeBuilders.filter(a => a.isExpandable && !a.isRemoved && a.activityDetails?.length > 0)
    },

    effectiveDateDefaultStart () {
      return moment().startOf('day').add(1, 'days')
    },

    scheduledRateChangeAlreadyExistsForContract () {
      return this.contract.scheduledRateChangeId > 0
    },

    showNewActivityButton () {
      return !this.scheduledRateChangeAlreadyExistsForContract
    },

    newDetailOutlinedStyle () {
      return {
        borderLeft: `4px solid ${colors.indigo.lighten2}`
      }
    },

    changedDetailOutlinedStyle () {
      return this.newDetailOutlinedStyle
    },

    showWarning () {
      return this.scheduledRateChangeAlreadyExistsForContract && !this.changeExistsWarningHidden && !this.readonly
    },

    focusedActivityIsObligation () {
      return this.focusedActivity?.activityTemplate?.specialization === TemplateSpecialization.Obligation.value
    }
  },

  async created () {
    this.initializeActivityChangeBuilders()
    this.setChosenEffectiveDate()
    if (this.contract?.tractId > 0) {
      this.tract = await this.fetchTract(this.contract.tractId)
      if (this.tract?.tractGroupId > 0) this.namedRates = await this.fetchNamedRates(this.tract.tractGroupId)
    }
  },

  watch: {
    contractActivityChangeBuilders: {
      handler () {
        this.$emit('changed', this.contractActivityChangeBuilders)
      },
      deep: true
    },

    chosenEffectiveDate (val) {
      this.$emit('effective-date-changed', val)
    }
  },

  methods: {
    formatMoney,
    companyOffsetToLocalDate,

    ...mapActions('activity-templates', ['fetchActivityTemplates']),
    ...mapActions('dialog', ['closeDialogsAtOrAbove', 'openOrUpdateDialog']),
    ...mapActions('scheduled-rate-change', ['deleteScheduledRateChanges', 'fetchScheduledRateChanges']),
    ...mapActions('tract', ['fetchNamedRates', 'fetchTract']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions']),

    async initializeActivityChangeBuilders () {
      const activitiesClone = cloneDeep(this.allActivities)
      this.sortDetails(activitiesClone)

      if (this.scheduledRateChangeAlreadyExistsForContract) {
        const currentScheduledRateChange = this.contractScheduledRateChanges.find(src => src.status === 0)
        if (currentScheduledRateChange.newActivities.length > 0) {
          this.activityTemplates = await this.fetchActivityTemplates()
        }
        currentScheduledRateChange.newActivities.forEach(na => {
          na.displayNewIcon = true
          if (na.payBy) {
            na.baseCost = new BaseCost({ rate: na.rate, payBy: na.payBy, payOn: na.payOn, rateSheet: na.rateSheet })
          }
          if (na.baseCost?.rateSheet?.rateSheetLines?.length > 0) {
            na.baseCost.rateSheet.rateSheetLines = na.baseCost.rateSheet.rateSheetLines.map(rateSheetLineFromServerToBuilder)
          }
          na.activityTemplate = this.activityTemplates.find(at => at.activityTemplateId === na.activityTemplateId)
          na.contractor = { name: na.account, accountId: na.accountId }
          na.activityDetails = cloneDeep(na.futureDetails)
          if (na.activityDetails) {
            na.activityDetails.forEach(d => {
              d.displayNewIcon = true
              d.cost = { rate: d.rate }
              d.product = { productId: d.productId, name: d.product }
            })
          }
          activitiesClone.push(na)
        })

        this.contractActivityChangeBuilders = activitiesClone.map(a => {
          if (a.payBy) {
            a.baseCost = new BaseCost({ rate: a.baseCost.rate, costCalculation: a.baseCost.costCalculation, payBy: a.baseCost.payBy, payOn: a.baseCost.payOn, rateSheet: a.baseCost.rateSheet })
          }
          if (a.baseCost?.rateSheet?.rateSheetLines?.length > 0) {
            a.baseCost.rateSheet.rateSheetLines = a.baseCost.rateSheet.rateSheetLines.map(rateSheetLineFromServerToBuilder)
          }

          const isRemoved = currentScheduledRateChange.deletedActivityIds.includes(a.activityId)
          if (isRemoved) {
            a.isRemoved = true
          }
          const isExpandable = !this.detailsDisallowedForActivity(a)
          const changedRateObj = currentScheduledRateChange.updatedActivities.find(csrc => csrc.activityId === a.activityId)
          if (changedRateObj) {
            a.displayChangedIcon = this.unequalAsFloat(changedRateObj.rate, a.baseCost.rate)
            a.baseCost.rate = changedRateObj.rate
          }

          const changedRateSheetObj = currentScheduledRateChange.updatedActivities.find(csrc => csrc.activityId === a.activityId && (csrc.rateSheet !== null && csrc.rateSheet !== undefined))
          if (changedRateSheetObj) {
            a.displayChangedRateSheetIcon = this.getRateSheetName(a) !== changedRateSheetObj.rateSheetName
            a.baseCost.rateSheet = changedRateSheetObj.rateSheet
            a.baseCost.rateSheetId = changedRateSheetObj.rateSheetId
          }

          a.activityDetails.forEach(d => {
            const changedActivityRateObj = currentScheduledRateChange.updatedActivities.find(csrc => csrc.activityId === a.activityId)
            if (!changedActivityRateObj) return undefined
            const changedDetailRateObj = changedActivityRateObj?.futureDetails.find(dr => dr.productId === d.product.productId)
            if (changedDetailRateObj) {
              d.displayChangedIcon = this.unequalAsFloat(changedDetailRateObj.rate, d.cost.rate)
              d.cost.rate = changedDetailRateObj.rate
            } else {
              d.isRemoved = true
            }
          })

          const activityScheduledChangeObject = currentScheduledRateChange.updatedActivities.find(csrc => csrc.activityId === a.activityId)
          if (!activityScheduledChangeObject) return { ...a, isExpandable }
          activityScheduledChangeObject.futureDetails.forEach(d => {
            const existingDetail = a.activityDetails.find(ad => ad.product.productId === d.productId)
            if (!existingDetail) {
              const newDetail = {
                cost: { rate: d.rate },
                product: { productId: d.productId, name: d.product },
                displayNewIcon: true
              }
              a.activityDetails.push(newDetail)
            }
          })

          a.deductFromActivityId = activityScheduledChangeObject.deductFromActivityId
          a.deductFromFutureActivityId = activityScheduledChangeObject.deductFromFutureActivityId
          return { ...a, isExpandable }
        })
      } else {
        this.contractActivityChangeBuilders = activitiesClone.map(a => {
          const isExpandable = !this.detailsDisallowedForActivity(a)
          return { ...a, isExpandable }
        })
      }
    },

    unequalAsFloat (a, b) {
      return parseFloat(a) !== parseFloat(b)
    },

    sortDetails (activities) {
      activities.forEach(a => {
        if (a.activityDetails) {
          a.activityDetails.sort((detailA, detailB) =>
            localeCompareAlphanumeric(detailA.product.name, detailB.product.name))
        }
      })
    },

    setChosenEffectiveDate () {
      if (this.scheduledRateChangeAlreadyExistsForContract) {
        const currentScheduledRateChange = this.contractScheduledRateChanges.find(src => src.status === ScheduledRateChangeStatus.Pending.value)
        this.chosenEffectiveDate = currentScheduledRateChange.effectiveDate
      } else {
        this.chosenEffectiveDate = this.effectiveDateDefaultStart
      }
    },

    costIcons (costType) {
      switch (costType) {
        case CostType.Unspecified.value:
          return 'mdi-alpha-u-circle-outline'
        case CostType.Production.value:
          return 'mdi-alpha-p-circle-outline'
        case CostType.Stumpage.value:
          return 'mdi-alpha-s-circle-outline'
        case CostType.NonInventory.value:
          return 'mdi-alpha-n-circle-outline'
      }
    },

    CostTypeFromTemplate (template) {
      const costType = CostType.forInt(template.costType)
      if (costType !== CostType.Production) return costType.name

      const productionCostType = ProductionCostType.forInt(template.productionType)
      return productionCostType === ProductionCostType.Other
        ? costType.name
        : `${costType.name} - ${productionCostType.name}`
    },

    showInsuranceExpiryIcon (item) {
      if (item?.contractor?.insuranceExpiryDate == null) return false
      const contractorInsuranceExpiryDateAsLocal = companyOffsetToLocalDate(item.contractor.insuranceExpiryDate, 'MM/DD/YYYY')
      const contractorInsuranceIsExpired = moment(contractorInsuranceExpiryDateAsLocal)
        .isSameOrBefore(moment().endOf('day'))
      return contractorInsuranceIsExpired
    },

    getInsuranceExpiryTooltip (item) {
      return `${this.$t('expiredOn')} ${companyOffsetToLocalDate(item.contractor.insuranceExpiryDate, 'MM/DD/YYYY')}`
    },

    counterpartySet (activity) {
      return activity.runtimeCounterparty === RuntimeCounterparty.Driver.value ||
      activity.contractor !== null ||
      (activity.runtimeCounterparty === RuntimeCounterparty.Landowner.value && this.tract?.landownerCount > 0)
    },

    runtimeCounterparty: (val) => RuntimeCounterparty.fromInt(val),

    counterpartyString (activity) {
      if (activity.runtimeCounterparty === RuntimeCounterparty.Driver.value) return this.$t('ticketDriver')
      return activity.contractor?.name ?? (this.tract.landOwnerAccountName || this.$t('multiple'))
    },

    accountingCategoryFromInt: (ac) => AccountingCategory.fromInt(ac),

    detailsDisallowedForActivity (activity) {
      return this.isApOrJePenalty(activity) ||
      activity.baseCost?.payBy === PayBy.Volume.value
    },

    isApOrJePenalty (activity) {
      return TemplateSpecialization.Penalty.value === activity.activityTemplate.specialization &&
      [AccountingCategory.Payable.value, AccountingCategory.Accrual.value].includes(activity.activityTemplate.category)
    },

    modifierFromInt (modifierValue) {
      return modifierValue === ActivityModifier.DistanceTraveled.value
        ? this.$t('distanceTraveledWithMiles', { distance: this.contract.distance })
        : ActivityModifier.fromInt(modifierValue)
    },

    editDetailRateClicked (detail) {
      this.detailChangeBuilderBeingEdited = detail
      this.activityChangeBuilderBeingEdited = this.contractActivityChangeBuilders.find(a =>
        a.activityDetails.some(d => d.activityDetailId === detail.activityDetailId))
      this.editingDetail = true
      this.openDialog()
    },

    editActivityClicked (activity) {
      this.activityChangeBuilderBeingEdited = this.contractActivityChangeBuilders.find(a => this.activitiesMatch(a, activity))
      this.editingActivity = true
      this.openDialog()
    },

    closeDialog () {
      this.viewingRateSheetDetails = undefined
      this.editingActivity = false
      this.editingDetail = false
      this.activityChangeBuilderBeingEdited = null
      this.detailChangeBuilderBeingEdited = null
      this.creatingActivity = false
      this.deleteWarningDialogOpen = false
      this.creatingDetail = false
      this.editingRateSheet = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog (width = 1000) {
      this.openOrUpdateDialog({ id: this.dialogId, width })
    },

    recoveryModeString (val) {
      return this.$t('recoverAdvancesWithDetail', {
        detail: val.advanceNotes ?? RecoveryMode.fromInt(val.recoveryMode)
      })
    },

    removeActivityClicked (activity) {
      if (!activity.isRemoved && this.activityDeductsFrom(activity)) {
        this.setSnackError(this.$t('cannotDeleteDeductingActivity'))
        return
      }

      let activitiesClone = cloneDeep(this.contractActivityChangeBuilders)

      const toRemove = activitiesClone.find(a => this.activitiesMatch(a, activity))

      const softRemove = toRemove.activityId > 0
      if (softRemove) {
        toRemove.isRemoved = !toRemove.isRemoved
        toRemove.isExpandable = toRemove.isRemoved ? false : !this.detailsDisallowedForActivity(toRemove)
      } else {
        activitiesClone = activitiesClone.filter(a => a.contractor?.accountId !== toRemove.contractor?.accountId ||
          a.activityTemplate.activityTemplateId !== toRemove.activityTemplate.activityTemplateId)

        if (activity.deductingActivities?.length > 0) {
          activitiesClone = activitiesClone.map(a => {
            const deductFromIdentifier = this.getDeductFromIdentifier(a)
            if (deductFromIdentifier == null) return a

            const deductsFromRemovedActivity = deductFromIdentifier.accountId === activity.contractor.accountId &&
              deductFromIdentifier.runtimeCounterparty === activity.runtimeCounterparty &&
              deductFromIdentifier.activityTemplateId === activity.activityTemplate.activityTemplateId

            if (deductsFromRemovedActivity) {
              a.deductFromActivityId = null
              a.deductFromActivityIdentifier = null
              a.hasBeenChanged = true
            }
            return a
          })
        }
      }
      this.contractActivityChangeBuilders = activitiesClone
    },

    newActivityClicked () {
      this.creatingActivity = true
      this.openDialog()
    },

    onActivityChangeConfirmed (updatedActivity) {
      const activitiesClone = cloneDeep(this.contractActivityChangeBuilders)
      const activity = activitiesClone.find(a => this.activitiesMatch(a, updatedActivity))
      activity.baseCost = updatedActivity.baseCost
      activity.hasBeenChanged = true
      activity.isExpandable = !this.detailsDisallowedForActivity(activity)

      if (updatedActivity.deductingActivities) {
        activitiesClone.forEach(a => {
          const originalDeductFromId = a.deductFromActivityId
          const originalDeductFromIdentifier = cloneDeep(a.deductFromActivityIdentifier)

          const currentlyDeductsFromThis = a.deductFromActivityId === activity.activityId ||
            (a.deductFromActivityIdentifier && this.activitiesMatch(activity, {
              contractor: { accountId: a.deductFromActivityIdentifier.accountId },
              runtimeCounterparty: a.deductFromActivityIdentifier.runtimeCounterparty,
              activityTemplate: { activityTemplateId: a.deductFromActivityIdentifier.activityTemplateId }
            }))

          const shouldDeductFromThis = updatedActivity.deductingActivities.some(da => this.activitiesMatch(a, da))

          if (shouldDeductFromThis) {
            a.deductFromActivityId = activity.activityId || null
            a.deductFromActivityIdentifier = activity.activityId ? null : {
              accountId: activity.contractor?.accountId,
              runtimeCounterparty: activity.runtimeCounterparty,
              activityTemplateId: activity.activityTemplate?.activityTemplateId
            }
          } else if (currentlyDeductsFromThis) {
            a.deductFromActivityId = null
            a.deductFromActivityIdentifier = null
          }

          const deductionChanged = originalDeductFromId !== a.deductFromActivityId ||
            JSON.stringify(originalDeductFromIdentifier) !== JSON.stringify(a.deductFromActivityIdentifier)

          if (deductionChanged) {
            a.hasBeenChanged = true
          }
        })
      }

      this.contractActivityChangeBuilders = activitiesClone
      this.closeDialog()
    },

    onDetailChangeConfirmed (updatedDetail) {
      const activitiesClone = cloneDeep(this.contractActivityChangeBuilders)
      const activity = activitiesClone.find(a => this.activitiesMatch(a, this.activityChangeBuilderBeingEdited))
      const detail = activity.activityDetails.find(d => d.activityDetailId === updatedDetail.activityDetailId)
      detail.cost = updatedDetail.cost
      detail.hasBeenChanged = true
      this.contractActivityChangeBuilders = activitiesClone
      this.closeDialog()
    },

    addDetailClicked (activity) {
      this.activityChangeBuilderBeingEdited = activity
      this.detailChangeBuilderBeingEdited = null
      this.creatingDetail = true
      this.openDialog()
    },

    hideChangeExistsWarning () {
      this.changeExistsWarningHidden = true
    },

    async onConfirmDeleteChange () {
      await this.deleteScheduledRateChanges(this.contract.contractId)
      this.deleteWarningDialogOpen = false
      this.closeDialog()
      await this.fetchScheduledRateChanges(this.contract.contractId)
      this.$emit('scheduled-rate-change-deleted')
    },

    deleteChangeButtonClicked () {
      this.deleteWarningDialogOpen = true
      this.openDialog(400)
    },

    getRateSheetName (activity) {
      let r = activity.baseCost?.rateSheet
      if (typeof r === 'string') return r

      r = activity.baseCost?.rateSheet?.name
      if (typeof r === 'string') return r

      r = activity.baseCost?.rateSheet?.rateSheetDefinition?.name
      return r || null
    },

    async rateSheetDetailClicked (activity) {
      const isReadonlyView = this.scheduledRateChangeAlreadyExistsForContract || this.focusedActivityIsObligation
      const hasInlineLines = activity.baseCost?.rateSheet?.rateSheetLines?.length > 0

      if (hasInlineLines && !isReadonlyView) {
        this.viewingRateSheetDetails = activity.baseCost.rateSheet
      } else if (activity.baseCost?.rateSheetId) {
        this.viewingRateSheetDetails = await this.fetchRateSheet(activity.baseCost.rateSheetId)
      } else {
        this.viewingRateSheetDetails = activity.baseCost?.rateSheet
      }

      this.focusedActivity = activity
      if (!this.accountRateSheetDefinitionsFetched) {
        this.accountRateSheetDefinitionsFetched = true
        this.fetchAccountRateSheetDefinitions(this.contract.destinationAccountId)
      }
      this.openDialog()
    },

    onActivityCreated (newActivity) {
      const notADuplicate = !this.contractActivityChangeBuilders.some(a =>
        a.activityTemplate?.activityTemplateId === newActivity.activityTemplate?.activityTemplateId &&
        a.contractor?.accountId === newActivity.contractor?.accountId)

      if (!notADuplicate) {
        this.setSnackError(this.$t('duplicateActivityBuilder'))
        return
      }

      newActivity.deductingActivities.forEach(da => {
        const existingActivity = this.contractActivityChangeBuilders.find(a => this.activitiesMatch(a, da))
        existingActivity.deductFromActivityIdentifier = {
          accountId: newActivity.contractor.accountId,
          runtimeCounterparty: newActivity.runtimeCounterparty,
          activityTemplateId: newActivity.activityTemplate.activityTemplateId
        }
        existingActivity.hasBeenChanged = true
      })

      if (!this.isImmediateRateChange) {
        newActivity = {
          activityDetails: newActivity.activityDetails ?? [],
          activityStatus: newActivity.activityTemplate.activityStatus,
          activityTemplateId: newActivity.activityTemplate.activityTemplateId,
          activityTemplate: newActivity.activityTemplate,
          advanceId: newActivity.advanceId,
          advanceNote: newActivity.advanceNote,
          baseCost: newActivity.baseCost,
          contractor: newActivity.contractor,
          deductingActivities: newActivity.deductingActivities,
          effectiveDate: this.chosenEffectiveDate,
          isExpandable: !this.detailsDisallowedForActivity(newActivity),
          recoveryMode: newActivity.recoveryMode.value,
          runtimeCounterparty: null,
          namedRateId: newActivity.namedRateId
        }
        newActivity.isNewlyAdded = true
      }
      newActivity.recoveryMode = newActivity.recoveryMode?.value ?? newActivity.recoveryMode
      this.contractActivityChangeBuilders.push(newActivity)
      this.closeDialog()
    },

    onDetailAdded (newDetail) {
      const activitiesClone = cloneDeep(this.contractActivityChangeBuilders)
      const activity = activitiesClone.find(a => this.activitiesMatch(a, this.activityChangeBuilderBeingEdited))
      const details = activity.activityDetails
      newDetail.isNewlyAdded = true
      if (Array.isArray(details)) {
        activity.activityDetails.push(newDetail)
      } else {
        activity.activityDetails = [newDetail]
      }
      activity.isExpandable = true
      this.contractActivityChangeBuilders = activitiesClone
      this.closeDialog()
    },

    handleDetailCostWarning (detail) {
      const activitiesClone = cloneDeep(this.contractActivityChangeBuilders)
      const activity = activitiesClone.find(a => this.activitiesMatch(a, this.activityChangeBuilderBeingEdited))
      activity.baseCost.rate = detail.cost.rate
      activity.hasBeenChanged = true
      this.contractActivityChangeBuilders = activitiesClone
      this.onDetailAdded(detail)
    },

    styleTableCellText (activity) {
      return {
        textDecoration: activity.isRemoved ? 'line-through' : 'none',
        opacity: activity.isRemoved ? 0.4 : 1
      }
    },

    styleDetailText (detail) {
      return {
        textDecoration: detail.isRemoved ? 'line-through' : 'none'
      }
    },

    styleDetailCard (detail) {
      let result = {
        opacity: detail.isRemoved ? 0.4 : 1
      }

      if (detail.displayNewIcon && this.scheduledRateChangeAlreadyExistsForContract) {
        result = { ...result, ...this.newDetailOutlinedStyle }
      } else if (detail.displayChangedIcon && this.scheduledRateChangeAlreadyExistsForContract) {
        result = { ...result, ...this.changedDetailOutlinedStyle }
      }

      return result
    },

    removeProductDetail (detail, activity) {
      const activitiesClone = cloneDeep(this.contractActivityChangeBuilders)
      const a = activitiesClone.find(acb => this.activitiesMatch(acb, activity))

      const softRemove = detail.activityDetailId > 0

      if (softRemove) {
        const d = a.activityDetails.find(ad => ad.product.productId === detail.product.productId)
        d.isRemoved = true
        a.hasBeenChanged = true
      } else {
        remove(a.activityDetails, d => d.product.productId === detail.product.productId)
      }
      a.isExpandable = a.activityDetails?.length > 0 && !this.detailsDisallowedForActivity(a)
      this.contractActivityChangeBuilders = activitiesClone
    },

    restoreProductDetail (detail, activity) {
      const activitiesClone = cloneDeep(this.contractActivityChangeBuilders)
      const a = activitiesClone.find(acb => this.activitiesMatch(acb, activity))
      const d = a.activityDetails.find(ad => ad.product.productId === detail.product.productId)
      d.isRemoved = false
      a.isExpandable = true
      this.contractActivityChangeBuilders = activitiesClone
    },

    activitiesMatch (a, b) {
      if (a.activityId && b.activityId) {
        return a.activityId === b.activityId
      } else {
        return a.activityTemplate?.activityTemplateId === b.activityTemplate?.activityTemplateId &&
            a.contractor?.accountId === b.contractor?.accountId &&
            a.runtimeCounterparty === b.runtimeCounterparty
      }
    },

    allowRemoveDetail () {
      const doNotAllowRemoval = this.scheduledRateChangeAlreadyExistsForContract
      if (doNotAllowRemoval) return false

      return true
    },

    iconForRecoveryMode (recoveryMode) {
      switch (recoveryMode) {
        case RecoveryMode.Oldest.value:
          return 'mdi-account-cash'
        case RecoveryMode.OldestOnTract.value:
          return 'mdi-crosshairs-gps'
        case RecoveryMode.Specific.value:
          return 'mdi-cash'
      }
      return ''
    },

    tooltipForRecoveryMode (activity) {
      switch (activity.recoveryMode) {
        case RecoveryMode.Oldest.value:
          return RecoveryMode.Oldest.name
        case RecoveryMode.OldestOnTract.value:
          return RecoveryMode.OldestOnTract.name
        case RecoveryMode.Specific.value:
          return this.$t('specificAdvanceWithNote', { note: activity.advanceNotes })
      }
      return ''
    },

    rateIsLowerThanBaseRate (detail, activity) {
      if (!detail.cost?.rate || !activity.baseCost?.rate) return false
      return detail.cost.rate < activity.baseCost.rate
    },

    activityDeductsFrom (activity) {
      return activity.deductFromActivityId != null ||
        activity.deductFromActivityIdentifier != null ||
        activity.deductFromFutureActivityId != null
    },

    getDeductFromIdentifier (activity) {
      if (activity.deductFromActivityId) {
        return { activityId: activity.deductFromActivityId }
      }
      if (activity.deductFromFutureActivityId) {
        return { futureActivityId: activity.deductFromFutureActivityId }
      }
      return activity.deductFromActivityIdentifier
    },

    hasDeductingChildren (activity) {
      return this.contractActivityChangeBuilders.some(a => {
        if (a.deductFromActivityId && activity.activityId) {
          return a.deductFromActivityId === activity.activityId
        }
        if (a.deductFromFutureActivityId && activity.futureActivityId) {
          return a.deductFromFutureActivityId === activity.futureActivityId
        }
        if (a.deductFromActivityIdentifier) {
          const identifierAsActivity = {
            contractor: { accountId: a.deductFromActivityIdentifier.accountId },
            runtimeCounterparty: a.deductFromActivityIdentifier.runtimeCounterparty,
            activityTemplate: { activityTemplateId: a.deductFromActivityIdentifier.activityTemplateId }
          }
          return this.activitiesMatch(activity, identifierAsActivity)
        }
        return false
      })
    },

    getDeductingChildren (activity) {
      return this.contractActivityChangeBuilders.filter(a => {
        if (a.deductFromActivityId && activity.activityId) {
          return a.deductFromActivityId === activity.activityId
        }
        if (a.deductFromFutureActivityId && activity.futureActivityId) {
          return a.deductFromFutureActivityId === activity.futureActivityId
        }
        if (a.deductFromActivityIdentifier) {
          const identifierAsActivity = {
            contractor: { accountId: a.deductFromActivityIdentifier.accountId },
            runtimeCounterparty: a.deductFromActivityIdentifier.runtimeCounterparty,
            activityTemplate: { activityTemplateId: a.deductFromActivityIdentifier.activityTemplateId }
          }
          return this.activitiesMatch(activity, identifierAsActivity)
        }
        return false
      })
    },

    setHoveredDeducting (parentActivity) {
      this.hoveredDeductingActivityIdentifiers = this.getDeductingChildren(parentActivity).map(a => ({
        activityId: a.activityId,
        futureActivityId: a.futureActivityId,
        accountId: a.contractor?.accountId,
        runtimeCounterparty: a.runtimeCounterparty,
        activityTemplateId: a.activityTemplate?.activityTemplateId
      }))
    },

    clearHoveredDeducting () {
      this.hoveredDeductingActivityIdentifiers = []
    },

    setHoveredParent (parentIdentifier) {
      this.hoveredParentActivityIdentifier = parentIdentifier
    },

    clearHoveredParent () {
      this.hoveredParentActivityIdentifier = null
    },

    deductionParentTooltip (activity) {
      const children = this.getDeductingChildren(activity)
      const tooltipCount = 2
      if (children.length === 0) return ''
      const names = children.slice(0, tooltipCount)
        .map(c => `${c.activityTemplate.name} - (${this.counterpartyStringForActivity(c)})`)
        .join(', ')
      const more = children.length > tooltipCount ? `, +${children.length - tooltipCount} more` : ''
      return `Deducted by: ${names}${more}`
    },

    deductionTooltip (activity) {
      let parentActivity
      if (activity.deductFromActivityId) {
        parentActivity = this.contractActivityChangeBuilders.find(a => a.activityId === activity.deductFromActivityId)
      } else if (activity.deductFromFutureActivityId) {
        parentActivity = this.contractActivityChangeBuilders.find(a => a.futureActivityId === activity.deductFromFutureActivityId)
      } else if (activity.deductFromActivityIdentifier) {
        const identifierAsActivity = {
          contractor: { accountId: activity.deductFromActivityIdentifier.accountId },
          runtimeCounterparty: activity.deductFromActivityIdentifier.runtimeCounterparty,
          activityTemplate: { activityTemplateId: activity.deductFromActivityIdentifier.activityTemplateId }
        }
        parentActivity = this.contractActivityChangeBuilders.find(a => this.activitiesMatch(a, identifierAsActivity))
      } else {
        return
      }
      if (!parentActivity) {
        return
      }
      const counterpartyString = this.counterpartyStringForActivity(parentActivity)
      const activityCounterpartyString = `${parentActivity.activityTemplate.name} - (${counterpartyString})`
      return `${this.$t('deductsFrom', { from: activityCounterpartyString })}`
    },

    rowClass (item) {
      const classes = []
      const isHoveredAsDeducting = this.hoveredDeductingActivityIdentifiers.some(id => {
        if (id.activityId && item.activityId) {
          return id.activityId === item.activityId
        }
        if (id.futureActivityId && item.futureActivityId) {
          return id.futureActivityId === item.futureActivityId
        }
        return id.accountId === item.contractor?.accountId &&
          id.runtimeCounterparty === item.runtimeCounterparty &&
          id.activityTemplateId === item.activityTemplate?.activityTemplateId
      })

      if (isHoveredAsDeducting) classes.push('activity-row-red-tint')

      if (this.hoveredParentActivityIdentifier) {
        const identifierAsActivity = {
          contractor: { accountId: this.hoveredParentActivityIdentifier.accountId },
          runtimeCounterparty: this.hoveredParentActivityIdentifier.runtimeCounterparty,
          activityTemplate: { activityTemplateId: this.hoveredParentActivityIdentifier.activityTemplateId }
        }
        const isHoveredAsParent = this.hoveredParentActivityIdentifier.activityId
          ? this.hoveredParentActivityIdentifier.activityId === item.activityId
          : this.hoveredParentActivityIdentifier.futureActivityId
            ? this.hoveredParentActivityIdentifier.futureActivityId === item.futureActivityId
            : this.activitiesMatch(item, identifierAsActivity)
        if (isHoveredAsParent) classes.push('activity-row-green-tint')
      }

      return classes.join(' ')
    },

    counterpartyStringForActivity (activity) {
      if (activity.runtimeCounterparty === RuntimeCounterparty.Driver.value) return this.$t('ticketDriver')
      if (activity.contractor?.name) return activity.contractor.name
      if (activity.runtimeCounterparty === RuntimeCounterparty.Landowner.value && this.tract?.landownerCount > 0) {
        return this.tract?.landOwnerAccountName || this.$t('multiple')
      }
      return this.$t('notSet')
    },

    rateSheetConfirmed (rateSheet) {
      this.focusedActivity.baseCost.rateSheet = RateSheet.fromServerResponse(rateSheet)
      this.focusedActivity.hasBeenChanged = true
      this.closeDialog()
    }
  }
}
</script>

<style>
.contract-rate-change-activity-table-table-container .v-data-table__wrapper table tbody tr.activity-row-red-tint > td {
  background-color: rgba(244, 67, 54, 0.12);
}

.contract-rate-change-activity-table-table-container .v-data-table__wrapper table tbody tr.activity-row-green-tint > td {
  background-color: rgba(76, 175, 80, 0.12);
}
</style>
